"use client"

import * as React from "react"
import { ChevronDown } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"

interface SeccionPlegableProps {
  /** El numero y la etiqueta solo existen en las secciones de primer nivel. */
  numero?: string
  etiqueta?: string
  titulo: string
  descripcion?: string
  /** Un icono delante del titulo. Lo pedian tres cabeceras de Agent Studio, y
   *  sin el hueco cada una se dibujaba entera a mano. */
  icono?: React.ReactNode
  /** Sin tarjeta: para una seccion DENTRO de otra. Una tarjeta dentro de una
   *  tarjeta es el antipatron mas comun del repositorio; un grupo de segundo
   *  nivel se marca con una linea y un titulo, no con otra caja. */
  plana?: boolean
  /** Abierta desde fuera. Si no se pasa, la seccion se gobierna sola. */
  abierta?: boolean
  onAlternar?: () => void
  /** Lo que va a la derecha de la cabecera, FUERA del boton: un icono de ayuda,
   *  un interruptor. Dentro no cabe: un boton no puede contener otro boton. */
  acciones?: React.ReactNode
  /** Un distintivo junto a la etiqueta: «Activa», «Pausada», «Vista local».
   *  Es parte de la cabecera y se pulsa con ella, asi que va DENTRO del boton
   *  -no es interactivo, solo dice en que estado esta la seccion-. Sin este
   *  hueco, unificar las cabeceras de Settings perdia ese dato. */
  estado?: React.ReactNode
  children: React.ReactNode
}

/**
 * Una seccion que se despliega, con la misma forma que las que ya habia: su
 * numero, su etiqueta, su titulo y su descripcion.
 *
 * Existia desde antes, y aun asi la cabecera estaba copiada a mano cinco veces
 * en ActionsPage -cada una con su `<button>` crudo y su cadena de clases-.
 * Copiar una cabecera no es solo repetir codigo: es cinco sitios donde cambiar
 * la altura del chevron, y cinco oportunidades de que una quede distinta.
 *
 * El `<button>` vive aqui, en la pieza compartida, que es el unico sitio donde
 * un elemento crudo esta bien: una cabecera que se despliega ES un boton
 * -necesita `aria-expanded` y responder al teclado-, pero no es el componente
 * `Button`, que trae su propia altura y su propio relleno.
 */
export function SeccionPlegable({
  numero,
  etiqueta,
  titulo,
  descripcion,
  icono,
  estado,
  plana = false,
  abierta,
  onAlternar,
  acciones,
  children,
}: SeccionPlegableProps) {
  const [abiertaPropia, setAbiertaPropia] = React.useState(false)
  const estaAbierta = abierta ?? abiertaPropia
  const alternar = onAlternar ?? (() => setAbiertaPropia((prev) => !prev))

  const cabecera = (
    <div className={plana ? "flex items-center gap-2" : "flex items-center gap-2 px-4 py-3"}>
      <button
        type="button"
        onClick={alternar}
        className="flex min-w-0 flex-1 items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/60"
        aria-expanded={estaAbierta}
      >
        <div className="min-w-0 space-y-1">
          {numero || etiqueta || estado ? (
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {numero ? (
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {numero}
                </span>
              ) : null}
              {etiqueta ? <span>{etiqueta}</span> : null}
              {estado}
            </div>
          ) : null}
          <div className="flex min-w-0 items-center gap-2">
            {icono}
            {plana ? (
              <h4 className="text-sm font-medium">{titulo}</h4>
            ) : (
              <h3 className="text-lg font-semibold text-foreground">{titulo}</h3>
            )}
          </div>
          {descripcion ? (
            <p className={plana ? "text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>
              {descripcion}
            </p>
          ) : null}
        </div>
        <div className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center">
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${estaAbierta ? "" : "-rotate-90"}`}
          />
        </div>
      </button>
      {acciones}
    </div>
  )

  const desplegado = estaAbierta ? (
    <div className="border-t px-4 py-5">{children}</div>
  ) : null

  if (plana) {
    return (
      <>
        {cabecera}
        {desplegado}
      </>
    )
  }

  return (
    <Card className="bg-card">
      <CardContent className="p-0">
        {cabecera}
        {desplegado}
      </CardContent>
    </Card>
  )
}
